import React, { useCallback, useEffect, useState } from "react";
import {
  InputField,
  PrivacyDisclaimer,
  PrivacyCheckbox,
  NotificationBanner,
  createClasses,
} from "@bloomberg/media-ui-core";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import { validEmail } from "~components/NewsletterForm/utils";
import { fetchNewsletterSubscriptions } from "~api/fetchNewsletterSubscriptions";
import { subscribeToNewsletter as subscribeToNewsletterApi } from "~api/subscribeToNewsletter";
import { FIVE_MINUTES } from "~constants";
import SuccessToast from "~components/NewsletterForm/SuccessToast";
import NewsletterSignUpButton from "~components/NewsletterForm/NewsletterSignUpButton";
import { addPageAction, logError } from "~utils/newrelic/newrelicBrowser";

const REGISTRATION_URL = process.env.NEXT_PUBLIC_REGISTRATION_URL;
const NEWSLETTER_SUBSCRIBE_URL = `${REGISTRATION_URL}/api/newsletters/subscribe`;

const COUNTRIES_TO_AUTO_CHECK = ["US", "GB", "FR", "BR", "CA"];
const INVALID_EMAIL_MESSAGE = "Invalid email address, please try again";
const SUBSCRIPTIONS_PAGE = "https://www.bloomberg.com/subscriptions";

interface NewsletterFormProps {
  id: string;
  countryCode: string;
  isSubscriberOnly?: boolean;
  listId?: string;
}

export const NewsletterForm = ({
  id,
  countryCode,
  isSubscriberOnly = false,
  listId,
}: NewsletterFormProps) => {
  const { session } = useRegUIClient();
  const queryClient = useQueryClient();

  const [anonEmail, setAnonEmail] = useState("");
  const [isChecked, setChecked] = useState(
    COUNTRIES_TO_AUTO_CHECK.includes(countryCode)
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isSignedUp, setIsSignedUp] = useState(false);

  const userEmail = session?.userInfo?.user?.email || "";
  const isUserSubscriber = session?.isBBGSubscriber;

  // Query to fetch newsletter subscription status
  const { data: newsletterSubscriptions } = useQuery({
    queryKey: ["newsletterSubscriptions", userEmail],
    queryFn: () => fetchNewsletterSubscriptions(userEmail),
    enabled: Boolean(userEmail),
    staleTime: FIVE_MINUTES,
  });

  useEffect(() => {
    const userSignedUp = listId
      ? newsletterSubscriptions?.userNewsletterLists?.includes(listId)
      : false;

    if (userSignedUp) {
      // Fires when a user is already signed up for the markets daily newsletter,
      // and that setting is reflected in the left rail by this module
      trackClickEvent(
        "Market_Data",
        "user_signedup_for_markets_daily",
        "uiaction",
        "Market Data"
      );
    }

    setIsSignedUp(Boolean(userSignedUp));
  }, [listId, newsletterSubscriptions]);

  // Mutation for subscribing to newsletter
  const subscribeMutation = useMutation({
    mutationFn: ({ email, optIn }: { email: string; optIn: string }) =>
      subscribeToNewsletterApi(
        NEWSLETTER_SUBSCRIBE_URL,
        { sourcePage: "HUB Page" },
        { email, listId, optIn }
      ),
    onSuccess: () => {
      setIsSignedUp(true);

      window.dataLayer?.push({
        event: "newsletter_signup",
        newsletterSignupIds: listId,
      });

      // clear user subscriptions cache
      queryClient.invalidateQueries({
        queryKey: ["newsletterSubscriptions", userEmail],
      });
    },
  });

  const handleSubscribeClick = useCallback(() => {
    trackClickEvent(
      "Market_Data",
      "markets_daily_signup",
      "click",
      "Market Data"
    );

    setErrorMessage("");
    const email = userEmail || anonEmail;

    addPageAction("marketsNewsLetterSubscriberClick");

    if (!validEmail(email)[0]) {
      setErrorMessage(INVALID_EMAIL_MESSAGE);

      return;
    }

    subscribeMutation.mutate({ email, optIn: isChecked ? "true" : "false" });
  }, [userEmail, anonEmail, isChecked, subscribeMutation]);

  const handleSubscriberOnlyClick = useCallback(() => {
    trackClickEvent(
      "Market_Data",
      "markets_daily_signup",
      "click",
      "Market Data"
    );

    addPageAction("marketsNewsLetterSubscriberOnlyClick");

    if (!userEmail || !isUserSubscriber) {
      window.open(SUBSCRIPTIONS_PAGE, "_blank", "noopener,noreferrer");

      return;
    }

    handleSubscribeClick();
  }, [userEmail, isUserSubscriber, handleSubscribeClick]);

  if (!listId) {
    logError(new Error(`No listId provided for NewsletterForm with id ${id}.`));

    return null;
  }

  const shouldShowEmailInput = !isSignedUp && !userEmail;

  return (
    <>
      {subscribeMutation.isError && (
        <NotificationBanner
          title="Sorry, something went wrong. Please try again."
          text="Please try again."
          variant="error"
        />
      )}

      {shouldShowEmailInput && !isSubscriberOnly && (
        <InputField
          id={`form-for-${id}`}
          type="email"
          placeholder="Enter your email"
          className={createClasses(styles.email, {
            [styles.error]: Boolean(errorMessage),
          })}
          onChange={(e) => setAnonEmail(e.target.value)}
          helperText={errorMessage}
          error={Boolean(errorMessage)}
          size="small"
        />
      )}

      {shouldShowEmailInput && (
        <PrivacyCheckbox
          checkboxProps={{ id: `privacy-checkbox-${id}` }}
          includeTextInHitArea
          checked={isChecked}
          onChange={() => setChecked((prev) => !prev)}
          className={styles.checkbox}
          countryCode={countryCode}
        />
      )}

      <NewsletterSignUpButton
        onClick={handleSubscribeClick}
        onSubscriberOnlyClick={handleSubscriberOnlyClick}
        isSignedUp={isSignedUp}
        isSubscriberOnly={isSubscriberOnly}
        isUserSubscriber={isUserSubscriber}
        isPending={subscribeMutation.isPending}
      />

      {shouldShowEmailInput && (
        <PrivacyDisclaimer
          countryCode={countryCode}
          className={styles.disclaimer}
        />
      )}

      <SuccessToast isOpen={subscribeMutation.isSuccess} />
    </>
  );
};
